import axios from 'axios';

// Configuração base da API
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  timeout: 60000, // 60 segundos para operações de corte que podem ser pesadas
  withCredentials: true, // Para cookies httpOnly
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor de request
api.interceptors.request.use(
  (config) => {
    // Adicionar token se existir
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Log da requisição em desenvolvimento
    if (process.env.NODE_ENV === 'development') {
      console.log(`🚀 ${config.method?.toUpperCase()} ${config.url}`, {
        data: config.data,
        params: config.params,
      });
    }

    return config;
  },
  (error) => {
    console.error('Erro no interceptor de request:', error);
    return Promise.reject(error);
  }
);

// Interceptor de response
api.interceptors.response.use(
  (response) => {
    // Log da resposta em desenvolvimento (não logar blobs)
    if (process.env.NODE_ENV === 'development' && response.config.responseType !== 'blob') {
      console.log(`✅ ${response.config.method?.toUpperCase()} ${response.config.url}`, {
        status: response.status,
        data: response.data,
      });
    }

    return response;
  },
  (error) => {
    // Log do erro em desenvolvimento
    if (process.env.NODE_ENV === 'development') {
      console.error(`❌ ${error.config?.method?.toUpperCase()} ${error.config?.url}`, {
        status: error.response?.status,
        data: error.response?.data,
      });
    }

    // Tratar erros específicos
    if (error.response?.status === 401) {
      // Token expirado ou inválido
      const errorCode = error.response?.data?.code;
      if (errorCode === 'TOKEN_EXPIRED') {
        // O interceptor do AuthContext vai lidar com isso
        return Promise.reject(error);
      } else {
        // Token inválido ou não autorizado
        // Não redirecionar automaticamente aqui para evitar "logout" abrupto.
        // Deixe o AuthContext decidir via /auth/me + refresh.
        return Promise.reject(error);
      }
    }

    if (error.response?.status === 403) {
      // Acesso negado
      console.warn('Acesso negado:', error.response.data);
    }

    if (error.response?.status >= 500) {
      // Erro do servidor
      console.error('Erro do servidor:', error.response.data);
    }

    if (error.code === 'NETWORK_ERROR') {
      console.error('Erro de rede. Verifique sua conexão.');
    }

    return Promise.reject(error);
  }
);

// Serviços específicos
export const authService = {
  login: (credentials) => api.post('/auth/login', credentials),
  logout: () => api.post('/auth/logout'),
  refreshToken: () => api.post('/auth/refresh-token'),
  me: () => api.get('/auth/me'),
};

export const userService = {
  getUsers: (params) => api.get('/users', { params }),
  getUser: (userId) => api.get(`/users/${userId}`),
  createUser: (userData) => api.post('/users', userData),
  updateUser: (userId, userData) => api.put(`/users/${userId}`, userData),
  deleteUser: (userId) => api.delete(`/users/${userId}`),
  permanentlyDeleteUser: (userId) => api.delete(`/users/${userId}/permanently`),
  changePassword: (userId, passwordData) => api.patch(`/users/${userId}/password`, passwordData),
  resetUserPassword: (userId, newPassword) => api.post(`/users/${userId}/reset-password`, { newPassword }),
  // Configuração de salário do funcionário
  getUserSalaryConfig: (userId) => api.get(`/users/${userId}/salary`),
  upsertUserSalaryConfig: (userId, data) => api.put(`/users/${userId}/salary`, data),
  calculateMonthlyOvertime: (userId, year, month) => api.get(`/users/${userId}/overtime`, { params: { year, month } }),
  // Aniversariantes
  getBirthdayUsers: () => api.get('/users/birthdays/week'),
  // Documentos
  getUserDocuments: (userId) => api.get(`/users/${userId}/documents`),
  uploadUserDocument: (userId, formData) => api.post(`/users/${userId}/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  downloadUserDocument: (userId, documentId) => api.get(`/users/${userId}/documents/${documentId}/download`, {
    responseType: 'blob'
  }),
  deleteUserDocument: (userId, documentId) => api.delete(`/users/${userId}/documents/${documentId}`),
};


export const clientService = {
  createClient: (data) => api.post('/clients', data),
  getClients: (params) => api.get('/clients', { params }),
  getClient: (id) => api.get(`/clients/${id}`),
  updateClient: (id, data) => api.put(`/clients/${id}`, data),
  deleteClient: (id) => api.delete(`/clients/${id}`),
};

export const firstContactService = {
  createFirstContact: (data) => api.post('/client-first-contact', data),
  getFirstContacts: (params) => api.get('/client-first-contact', { params }),
  getFirstContact: (id) => api.get(`/client-first-contact/${id}`),
  updateFirstContact: (id, data) => api.put(`/client-first-contact/${id}`, data),
  deleteFirstContact: (id) => api.delete(`/client-first-contact/${id}`),
  getStats: (params) => api.get('/client-first-contact/stats', { params }),
};

export const settingsService = {
  getClientCreateAllowedRoles: () => api.get('/settings/client-access'),
  updateClientCreateAllowedRoles: (roles) => api.put('/settings/client-access', { roles }),

  // Configurações de Apresentação
  getPresentationSettings: () => api.get('/settings/presentation'),
  updatePresentationSettings: (settings) => api.put('/settings/presentation', settings),

  // Configurações Institucionais da Empresa
  getCompanySettings: () => api.get('/settings/company'),
  updateCompanySettings: (settings) => api.put('/settings/company', settings),
};

export const workScheduleService = {
  // Listar jornadas de trabalho
  getWorkSchedules: (params) => api.get('/work-schedules', { params }),

  // Obter jornada por ID
  getWorkSchedule: (id) => api.get(`/work-schedules/${id}`),

  // Obter jornadas de um funcionário
  getWorkSchedulesByUser: (userId, params) => api.get(`/work-schedules/user/${userId}`, { params }),

  // Criar jornada de trabalho
  createWorkSchedule: (data) => api.post('/work-schedules', data),

  // Atualizar jornada de trabalho
  updateWorkSchedule: (id, data) => api.put(`/work-schedules/${id}`, data),

  // Deletar jornada de trabalho
  deleteWorkSchedule: (id) => api.delete(`/work-schedules/${id}`),
};

export const payslipService = {
  // Gerar prévia de holerite
  generatePreview: (data) => api.post('/payslips/generate', data),

  // Gerar prévia em massa
  generateBulkPreview: (data) => api.post('/payslips/generate-bulk', data),

  // Listar holerites
  getPayslips: (params) => api.get('/payslips', { params }),

  // Obter holerite específico
  getPayslip: (payslipId) => api.get(`/payslips/${payslipId}`),

  // Atualizar holerite (edição pelo gerente)
  updatePayslip: (payslipId, data) => api.put(`/payslips/${payslipId}`, data),

  // Aprovar holerite
  approvePayslip: (payslipId, data) => api.post(`/payslips/${payslipId}/approve`, data),

  // Registrar / atualizar pagamento do holerite com comprovante
  recordPayslipPayment: (payslipId, formData) => api.post(`/payslips/${payslipId}/payment`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),

  // Aprovar holerites em massa
  approveBulk: (data) => api.post('/payslips/approve-bulk', data),

  // Rejeitar holerite
  rejectPayslip: (payslipId, data) => api.post(`/payslips/${payslipId}/reject`, data),

  // Deletar holerite
  deletePayslip: (payslipId) => api.delete(`/payslips/${payslipId}`),

  // ========== 13º SALÁRIO ==========
  // Resumo anual de 13º salário
  getThirteenthSummary: (year) => api.get('/payslips/thirteenth/summary', { params: { year } }),

  // Prévia de 13º (parcela 1 ou 2)
  previewThirteenth: (data) => api.post('/payslips/thirteenth/preview', data),

  // Gerar 13º individual
  generateThirteenth: (data) => api.post('/payslips/thirteenth/generate', data),

  // Gerar 13º em lote
  generateThirteenthBulk: (data) => api.post('/payslips/thirteenth/generate-bulk', data),

  // ========== RESCISÃO CONTRATUAL ==========
  // Prévia de Rescisão
  previewTermination: (data) => api.post('/payslips/termination/preview', data),

  // Confirmar / homologar Rescisão
  confirmTermination: (data) => api.post('/payslips/termination/confirm', data),

  // ========== EXPORTAÇÃO CONTÁBIL E ESOCIAL ==========
  // Exportar folha contábil (CSV / JSON)
  exportAccounting: (params) => api.get('/payslips/export/accounting', { params, responseType: params?.format === 'json' ? 'json' : 'blob' }),

  // Informe de Rendimentos Anual / DIRF
  getAnnualEarningsReport: (userId, params) => api.get(`/payslips/annual-report/${userId}`, { params }),

  // Resumo de Eventos Periódicos eSocial (S-1200, S-1210, S-2299)
  getESocialSummary: (params) => api.get('/payslips/esocial/summary', { params }),
};

export const appointmentService = {
  // Listar agendamentos com filtros e paginação
  getAppointments: (params) => api.get('/appointments', { params }),

  // Obter agendamento específico
  getAppointment: (id) => api.get(`/appointments/${id}`),

  // Criar novo agendamento
  createAppointment: (data) => api.post('/appointments', data),

  // Atualizar agendamento completo
  updateAppointment: (id, data) => api.put(`/appointments/${id}`, data),

  // Atualizar apenas o status do agendamento
  updateAppointmentStatus: (id, status) => api.patch(`/appointments/${id}/status`, { status }),

  // Remarcar agendamento de apresentação 3D
  reschedulePresentationAppointment: (id, data) => api.post(`/appointments/${id}/reschedule`, data),

  // Deletar agendamento
  deleteAppointment: (id) => api.delete(`/appointments/${id}`),

  // Obter estatísticas de agendamentos
  getAppointmentStats: (params) => api.get('/appointments/stats', { params }),

  // Obter pipeline de status para um tipo de entidade
  getStatusPipeline: (entityType) => api.get(`/status-pipeline/${entityType}`),
};

export const timeClockService = {
  // Bater ponto (entrada)
  clockIn: (data) => api.post('/time-clock/clock-in', data),

  // Bater ponto (saída)
  clockOut: (data) => api.post('/time-clock/clock-out', data),

  // Iniciar intervalo
  startBreak: () => api.post('/time-clock/break/start'),

  // Finalizar intervalo
  endBreak: () => api.post('/time-clock/break/end'),

  // Obter registro de ponto atual
  getCurrentRecord: () => api.get('/time-clock/current'),

  // Listar registros de ponto
  getRecords: (params) => api.get('/time-clock/records', { params }),
};

export const workedSaturdaysService = {
  // Listar sábados trabalhados
  getWorkedSaturdays: (params) => api.get('/worked-saturdays', { params }),

  // Marcar/desmarcar sábado
  toggleWorkedSaturday: (userId, data) => api.post(`/worked-saturdays/${userId}/toggle`, data),

  // Marcar múltiplos sábados
  bulkToggleWorkedSaturdays: (userId, data) => api.post(`/worked-saturdays/${userId}/bulk`, data),
};

export const overtimeRecordsService = {
  // Listar registros de horas extras
  getOvertimeRecords: (params) => api.get('/overtime-records', { params }),

  // Criar ou atualizar registro de horas extras
  upsertOvertimeRecord: (userId, data) => api.post(`/overtime-records/${userId}`, data),

  // Criar ou atualizar múltiplos registros
  bulkUpsertOvertimeRecords: (userId, data) => api.post(`/overtime-records/${userId}/bulk`, data),

  // Obter relatório mensal (status de aprovação)
  getMonthlyReport: (params) => api.get('/overtime-records/monthly-report', { params }),

  // Obter relatório mensal por ID
  getReportById: (id) => api.get(`/overtime-records/report/${id}`),

  // Aprovar relatório mensal
  approveMonthlyReport: (data) => api.post('/overtime-records/approve-monthly-report', data),

  // Aprovar relatórios de múltiplos usuários em lote
  bulkApproveMonthlyReports: (data) => api.post('/overtime-records/bulk-approve-monthly-reports', data),

  // Criar horas extras para múltiplos usuários de uma vez (cadastro em lote)
  bulkCreateMultipleUsers: (data) => api.post('/overtime-records/bulk-multi-user', data),

  // Deletar registros de horas extras em lote
  deleteBulkOvertimeRecords: (data) => api.post('/overtime-records/delete-bulk', data),
};

export const advanceService = {
  // Solicitar ou atualizar adiantamento
  requestAdvance: (data) => api.post('/advance/request', data),

  // Obter solicitação atual do usuário
  getMyRequest: () => api.get('/advance/my-request'),

  // Listar solicitações pendentes (apenas GERENCIA)
  getPendingRequests: (page = 1, limit = 20) =>
    api.get('/advance/pending', { params: { page, limit } }),

  // Listar solicitações aprovadas (apenas GERENCIA)
  getApprovedRequests: (params) =>
    api.get('/advance/approved', { params }),

  // Aprovar solicitação (apenas GERENCIA)
  approveRequest: (requestId) => api.post(`/advance/${requestId}/approve`),

  // Rejeitar solicitação (apenas GERENCIA)
  rejectRequest: (requestId, data) => api.post(`/advance/${requestId}/reject`, data),

  // Cancelar e estornar solicitação aprovada no caixa (apenas GERENCIA)
  cancelApprovedRequest: (requestId, data = {}) => api.post(`/advance/${requestId}/cancel`, data),

  // Criar adiantamento manual (apenas GERENCIA)
  createManualAdvance: (data) => api.post('/advance/manual', data),

  // Aprovar múltiplas solicitações (apenas GERENCIA)
  bulkApproveRequests: (requestIds) => api.post('/advance/bulk-approve', { requestIds }),
};

export const appointmentSessionsService = {
  // Iniciar sessão de atendimento
  startSession: (data) => api.post('/appointment-sessions/start', data),

  // Finalizar sessão de atendimento
  endSession: (data) => api.post('/appointment-sessions/end', data),

  // Obter sessões do dia
  getTodaySessions: () => api.get('/appointment-sessions/today'),

  // Obter sessão atual (em andamento)
  getCurrentSession: () => api.get('/appointment-sessions/current'),

  // Obter agendamentos do dia
  getTodayAppointments: () => api.get('/appointment-sessions/today-appointments'),
};

export const holidaysService = {
  // Listar feriados
  getHolidays: (params) => api.get('/holidays', { params }),

  // Buscar feriado por ID
  getHolidayById: (id) => api.get(`/holidays/${id}`),

  // Criar feriado
  createHoliday: (data) => api.post('/holidays', data),

  // Atualizar feriado
  updateHoliday: (id, data) => api.put(`/holidays/${id}`, data),

  // Deletar feriado
  deleteHoliday: (id) => api.delete(`/holidays/${id}`),
};

export const absencesService = {
  // Listar divergências
  getAbsences: (params) => api.get('/absences', { params }),

  // Buscar divergência por ID
  getAbsenceById: (id) => api.get(`/absences/${id}`),

  // Criar divergência (com upload de arquivo)
  createAbsence: (data, file) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => {
      if (data[key] !== null && data[key] !== undefined) {
        formData.append(key, data[key]);
      }
    });
    if (file) {
      formData.append('document', file);
    }
    return api.post('/absences', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  // Atualizar divergência (com upload de arquivo)
  updateAbsence: (id, data, file) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => {
      if (data[key] !== null && data[key] !== undefined) {
        formData.append(key, data[key]);
      }
    });
    if (file) {
      formData.append('document', file);
    }
    return api.put(`/absences/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  // Deletar divergência
  deleteAbsence: (id) => api.delete(`/absences/${id}`),
};

export const extraAdvancesService = {
  // Listar vales
  getExtraAdvances: (params) => api.get('/extra-advances', { params }),

  // Meus vales extras (self-service)
  getMyExtraAdvances: () => api.get('/extra-advances/my-extras'),

  // Buscar vale por ID
  getExtraAdvanceById: (id) => api.get(`/extra-advances/${id}`),

  // Criar vale
  createExtraAdvance: (data) => api.post('/extra-advances', data),

  // Atualizar vale
  updateExtraAdvance: (id, data) => api.put(`/extra-advances/${id}`, data),

  // Deletar vale
  deleteExtraAdvance: (id) => api.delete(`/extra-advances/${id}`),
};

export const recurringBonusesService = {
  // Listar bônus recorrentes
  getRecurringBonuses: (params) => api.get('/recurring-bonuses', { params }),

  // Meus bônus recorrentes (self-service)
  getMyRecurringBonuses: () => api.get('/recurring-bonuses/my-bonuses'),

  // Buscar bônus recorrente por ID
  getRecurringBonusById: (id) => api.get(`/recurring-bonuses/${id}`),

  // Criar bônus recorrente
  createRecurringBonus: (data) => api.post('/recurring-bonuses', data),

  // Atualizar bônus recorrente
  updateRecurringBonus: (id, data) => api.put(`/recurring-bonuses/${id}`, data),

  // Deletar bônus recorrente
  deleteRecurringBonus: (id) => api.delete(`/recurring-bonuses/${id}`),

  // Ativar/desativar bônus recorrente
  toggleRecurringBonus: (id, isActive) => api.post(`/recurring-bonuses/${id}/toggle`, { isActive }),

  // Gerar bônus recorrentes para um mês específico
  generateRecurringBonuses: (year, month) => api.post('/recurring-bonuses/generate', { year, month }),
};

export const taxContributionsService = {
  // Listar contribuições fiscais
  getTaxContributions: (params) => api.get('/tax-contributions', { params }),

  // Buscar contribuição fiscal por ID
  getTaxContributionById: (id) => api.get(`/tax-contributions/${id}`),

  // Criar ou atualizar contribuição fiscal (com upload de arquivos)
  upsertTaxContribution: (data) => {
    const formData = new FormData();

    // Adicionar campos de texto
    Object.keys(data).forEach(key => {
      if (key !== 'inss_guide' && key !== 'inss_receipt' && key !== 'fgts_guide' && key !== 'fgts_receipt') {
        if (data[key] !== null && data[key] !== undefined) {
          formData.append(key, data[key]);
        }
      }
    });

    // Adicionar arquivos
    if (data.inss_guide) formData.append('inss_guide', data.inss_guide);
    if (data.inss_receipt) formData.append('inss_receipt', data.inss_receipt);
    if (data.fgts_guide) formData.append('fgts_guide', data.fgts_guide);
    if (data.fgts_receipt) formData.append('fgts_receipt', data.fgts_receipt);

    return api.post('/tax-contributions', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  // Atualizar contribuição fiscal (com upload de arquivos)
  updateTaxContribution: (id, data) => {
    const formData = new FormData();

    // Adicionar campos de texto
    Object.keys(data).forEach(key => {
      if (key !== 'inss_guide' && key !== 'inss_receipt' && key !== 'fgts_guide' && key !== 'fgts_receipt') {
        if (data[key] !== null && data[key] !== undefined) {
          formData.append(key, data[key]);
        }
      }
    });

    // Adicionar arquivos
    if (data.inss_guide) formData.append('inss_guide', data.inss_guide);
    if (data.inss_receipt) formData.append('inss_receipt', data.inss_receipt);
    if (data.fgts_guide) formData.append('fgts_guide', data.fgts_guide);
    if (data.fgts_receipt) formData.append('fgts_receipt', data.fgts_receipt);

    return api.put(`/tax-contributions/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  // Deletar contribuição fiscal
  deleteTaxContribution: (id) => api.delete(`/tax-contributions/${id}`),

  // Relatório consolidado de encargos da empresa (Patronal + Segurados + FGTS)
  getTaxConsolidation: (params) => api.get('/tax-contributions/consolidation', { params }),

  // Sincronizar automaticamente da folha de pagamento do mês/ano
  autoSyncTaxContributions: (data) => api.post('/tax-contributions/auto-sync', data),
};

export const inssTablesService = {
  getInssTables: (params) => api.get('/inss-tables', { params }),
  getInssTableByYear: (year) => api.get(`/inss-tables/year/${year}`),
  upsertInssTable: (data) => api.post('/inss-tables', data),
  updateInssTable: (id, data) => api.put(`/inss-tables/${id}`, data),
  deleteInssTable: (id) => api.delete(`/inss-tables/${id}`),
  activateInssTable: (year) => api.post(`/inss-tables/year/${year}/activate`),
};

export const expenseTypesService = {
  // Listar tipos de despesas
  getExpenseTypes: (params) => api.get('/expense-types', { params }),

  // Obter tipo de despesa
  getExpenseType: (id) => api.get(`/expense-types/${id}`),

  // Criar tipo de despesa
  createExpenseType: (data) => api.post('/expense-types', data),

  // Atualizar tipo de despesa
  updateExpenseType: (id, data) => api.put(`/expense-types/${id}`, data),

  // Deletar tipo de despesa
  deleteExpenseType: (id) => api.delete(`/expense-types/${id}`),
};

export const expensesService = {
  // Listar despesas
  getExpenses: (params) => api.get('/expenses', { params }),

  // Meus vales/empréstimos (self-service)
  getMyExpenses: () => api.get('/expenses/my-expenses'),

  // Obter despesa
  getExpense: (id) => api.get(`/expenses/${id}`),

  // Criar despesa
  createExpense: (data) => api.post('/expenses', data),

  // Atualizar despesa
  updateExpense: (id, data) => api.put(`/expenses/${id}`, data),

  // Marcar parcela como paga
  markInstallmentPaid: (id, installmentId) => api.patch(`/expenses/${id}/installments/${installmentId}/paid`),

  // Quitação manual antecipada / liquidação de parcelas com injeção no caixa
  settleExpenseManual: (id, data) => api.post(`/expenses/${id}/settle-manual`, data),

  // Deletar despesa
  deleteExpense: (id) => api.delete(`/expenses/${id}`),

  // Aprovar despesa
  approveExpense: (id) => api.post(`/expenses/${id}/approve`),

  // Recalcular parcelas com valor exato
  recalculateInstallments: (id) => api.post(`/expenses/${id}/recalculate-installments`),
};

export const profitSharingService = {
  // Listar PLR
  getProfitSharing: (params) => api.get('/profit-sharing', { params }),

  // Obter PLR
  getProfitSharingById: (id) => api.get(`/profit-sharing/${id}`),

  // Criar/Atualizar PLR
  upsertProfitSharing: (data) => api.post('/profit-sharing', data),

  // Aprovar PLR
  approveProfitSharing: (id) => api.patch(`/profit-sharing/${id}/approve`),

  // Marcar PLR como paga
  markProfitSharingPaid: (id) => api.patch(`/profit-sharing/${id}/paid`),

  // Deletar PLR
  deleteProfitSharing: (id) => api.delete(`/profit-sharing/${id}`),

  // Gerar holerites PLR
  generatePLRPayslips: (data) => api.post('/profit-sharing/generate-payslips', data),
};

// Serviço de documentos fiscais
export const fiscalDocumentsService = {
  // Listar documentos
  getDocuments: (params) => api.get('/fiscal-documents', { params }),

  // Obter categorias ativas (atalho direto)
  getCategories: (params) => api.get('/fiscal-categories', { params }),

  // Obter estatísticas
  getStats: () => api.get('/fiscal-documents/stats'),

  // Obter documento específico
  getDocument: (id) => api.get(`/fiscal-documents/${id}`),

  // Upload de documento
  uploadDocument: (formData) => api.post('/fiscal-documents', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),

  // Atualizar documento
  updateDocument: (id, data) => api.put(`/fiscal-documents/${id}`, data),

  // Substituir arquivo do documento
  replaceFile: (id, formData) => api.put(`/fiscal-documents/${id}/file`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),

  // Deletar documento
  deleteDocument: (id) => api.delete(`/fiscal-documents/${id}`),

  // Download de documento
  downloadDocument: (id) => api.get(`/fiscal-documents/${id}/download`, {
    responseType: 'blob'
  }),
};

// Serviço de categorias de documentos fiscais
export const fiscalCategoriesService = {
  // Listar categorias
  getCategories: (params) => api.get('/fiscal-categories', { params }),

  // Obter categoria específica
  getCategory: (id) => api.get(`/fiscal-categories/${id}`),

  // Criar categoria
  createCategory: (data) => api.post('/fiscal-categories', data),

  // Atualizar categoria
  updateCategory: (id, data) => api.put(`/fiscal-categories/${id}`, data),

  // Deletar categoria
  deleteCategory: (id) => api.delete(`/fiscal-categories/${id}`),
};

// Serviço de Pré-Projetos
export const preProjectService = {
  // Listar todos os pré-projetos com filtros
  getAll: (params) => api.get('/pre-projects', { params }),

  // Obter pré-projeto específico
  getById: (id) => api.get(`/pre-projects/${id}`),

  // Criar novo pré-projeto
  create: (data) => api.post('/pre-projects', data),

  // Atualizar pré-projeto
  update: (id, data) => api.put(`/pre-projects/${id}`, data),

  // Atualizar status
  updateStatus: (id, data) => api.patch(`/pre-projects/${id}/status`, data),

  // Revisar pré-projeto (GERENCIA)
  review: (id, data) => api.post(`/pre-projects/${id}/review`, data),

  // Converter pré-projeto em projeto (após aprovação do cliente)
  convertToProject: (id) => api.post(`/pre-projects/${id}/convert`),

  // Obter estatísticas
  getStats: () => api.get('/pre-projects/stats'),

  // Obter lista de statuses
  getStatuses: () => api.get('/pre-projects/statuses'),
};

// Schedule Blocks Service
export const scheduleBlocksService = {
  getBlocks: (params) => api.get('/schedule-blocks', { params }),
  getBlock: (id) => api.get(`/schedule-blocks/${id}`),
  createBlock: (data) => api.post('/schedule-blocks', data),
  updateBlock: (id, data) => api.put(`/schedule-blocks/${id}`, data),
  deleteBlock: (id) => api.delete(`/schedule-blocks/${id}`),
};

// Block Permissions Service
export const blockPermissionsService = {
  getPermissions: () => api.get('/block-permissions'),
  updatePermission: (role, data) => api.put(`/block-permissions/${role}`, data),
};

export default api; 